import React, { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { RequireAuthShell } from '../shells/RequireAuthShell';

/**
 * Props for the RequireAuthContainer component
 */
export interface RequireAuthContainerProps {
  /** The content to render if authenticated */
  children: ReactNode;
  /** Optional CSS class name for the loading spinner container */
  className?: string;
  /** Optional redirect path when not authenticated (defaults to '/auth') */
  redirectPath?: string;
}

/**
 * RequireAuthContainer component
 * 
 * A container component that manages authentication requirements.
 * Handles:
 * - Authentication state checking
 * - Redirection for unauthenticated users
 * - Loading state management
 */
export const RequireAuthContainer: React.FC<RequireAuthContainerProps> = ({
  children,
  className,
  redirectPath = '/auth'
}) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If not loading and no user, redirect to auth page
    if (!loading && !user) {
      navigate(redirectPath, { replace: true });
    }
  }, [user, loading, navigate, redirectPath]);

  return (
    <RequireAuthShell
      isLoading={loading}
      isAuthenticated={!!user}
      className={className}
    >
      {children}
    </RequireAuthShell>
  );
}; 